import type React from 'react';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import type { ShaderName, ShaderModel } from '../../types';
import { shaderModels } from '../../types';
import type { FormValues } from '@shadeables/shadermodel';
import { useCurrentPlan } from '@shadeables/billing';
import { useCurrentProject } from '../../hooks/useCurrentProject';

interface ProjectContextValue {
  selectedShader: ShaderName | null;
  shaderModel: ShaderModel | null;
  formValues: FormValues;
  setSelectedShader: (shader: ShaderName) => void;
  updateFormValues: (values: FormValues) => void;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjectContext must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: React.ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({
  children,
}) => {
  const { data: project } = useCurrentProject();
  const { canAccess3DShaders } = useCurrentPlan();

  const [selectedShader, setSelectedShader] = useState<ShaderName | null>(null);
  const [shaderModel, setShaderModel] = useState<ShaderModel | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({});

  // Initialize shader from project config on first load only
  useEffect(() => {
    if (!project || selectedShader !== null) return;

    // Set the selected shader from project config if available, otherwise default to 'fire'
    const shaderToSet =
      project.config?.name && project.config.name in shaderModels
        ? (project.config.name as ShaderName)
        : 'fire';

    setSelectedShader(shaderToSet);
  }, [project, selectedShader]);

  // Update shader model whenever selectedShader changes
  useEffect(() => {
    if (!selectedShader || !project) return; // Wait for both project and selectedShader

    const CurrentShader = shaderModels[selectedShader];

    // Use saved project config if it matches the current shader
    let configToUse = {};
    if (project.config?.name === selectedShader && project.config) {
      configToUse = project.config;
    }

    const model = new CurrentShader.Model(configToUse);
    setShaderModel(model);

    // Extract form values from the model
    const values: FormValues = {};
    Object.entries(model).forEach(([key, value]) => {
      if (key !== 'name' && key !== 'generate' && typeof value !== 'function') {
        values[key] = value;
      }
    });
    setFormValues(values);
  }, [selectedShader, project]);

  const updateFormValues = useCallback(
    (newValues: FormValues) => {
      setFormValues(newValues);

      // Update the shader model with new values
      if (shaderModel && selectedShader) {
        const CurrentShader = shaderModels[selectedShader];
        // FormValues is compatible with shader constructor since both use Record<string, primitive>
        const updatedModel = new CurrentShader.Model(newValues);
        setShaderModel(updatedModel);
      }
    },
    [shaderModel, selectedShader]
  );

  const handleSetSelectedShader = useCallback(
    (shader: ShaderName) => {
      // Check if trying to select a 3D shader without permission
      const shaderInfo = shaderModels[shader];
      if (shaderInfo.type === '3d' && !canAccess3DShaders) {
        // Silently reset to a 2D shader (fire as default)
        console.warn('3D shaders require a paid plan. Resetting to 2D shader.');
        setSelectedShader('fire');
        return;
      }

      setSelectedShader(shader);
      // Reset form values when changing shaders - useEffect will handle the rest
    },
    [canAccess3DShaders]
  );

  const value: ProjectContextValue = useMemo(
    () => ({
      selectedShader,
      shaderModel,
      formValues,
      setSelectedShader: handleSetSelectedShader,
      updateFormValues,
    }),
    [
      selectedShader,
      shaderModel,
      formValues,
      handleSetSelectedShader,
      updateFormValues,
    ]
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
};
